import React, { useCallback, useEffect, useRef, useState } from "react";
import CreateUserModal from "./components/create-user-modal";
import { ApiResponse } from "../core/_models";
import { useSnackbar } from "../../../_theme/context/SnackbarContext";
import CustomeDynamicTable from "../apps/components/CustomDynamicTable";
import { fetchUserList } from "./core/_request";
import { User } from "./core/_models";
import CustomFormInput from "../apps/components/CustomFormInput";
import { Button } from "react-bootstrap";
import { KTIcon } from "../../../_theme/helpers";
import { useAuth } from "../auth";
import DataTableSkeleton from "../apps/components/skeleton/DataTableSkeleton";
import { getApiErrorMessage } from "../../../utils/utils";
import { isAbortedError } from "../../../utils/global";

const UserTable: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const [users, setUsers] = React.useState<User[]>([]);
  const [search, setSearch] = React.useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(10);
  const [total, setTotal] = React.useState<number>(0);
  const [selectedData, setSelectedData] = React.useState<User | null>(null);
  const [isUpdateMode, setIsUpdateMode] = React.useState<boolean>(false)
  const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const { auth } = useAuth();
  const abortRef = useRef<AbortController | null>(null);

  const [sortKey, setSortKey] = useState<string>('fullname');
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>('asc');

  const handleSortChange = (key: string, order: "asc" | "desc" | null) => {
    setPage(1);
    setSortKey(key);
    setSortOrder(order);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 500); // 400–600ms ideal UX

    return () => clearTimeout(timer);
  }, [search]);

  const handleFecthUsers = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const userData = await fetchUserList(
        { page: page, limit: perPage, search: debouncedSearch, sort_by: sortKey, sort_order: sortOrder, },
        controller.signal
      );
      if (controller.signal.aborted) return;
      if (userData.status == "ok") {
        setUsers(userData.data.list);
        setTotal(userData.data.total);
      } else {
        userData.message && showSnackbar(userData.message, "danger");
      }
    } catch (error) {
      if (isAbortedError(error)) return;
      console.error("Failed to fetch Users:", error);
      showSnackbar(getApiErrorMessage(error, "Failed to fetch Users:"), "danger");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [page, perPage, debouncedSearch, sortKey, sortOrder, showSnackbar]);

  const columns = [
    { key: "sr", label: "SR", align: "center" },
    { key: "title", label: "Title", align: "center" },
    { key: "fullname", label: "Full Name", sortable: true },
    { key: "gender", label: "Gender", align: "center", render: (user: User) => user.gender == 'M' ? <>Male</> : <>Female</>, sortable: true },
    { key: "email", label: "Email" }
  ]
  if (auth?.isAdmin == 1) {
    const actioncolumns = {
      key: "actions", label: "Actions", align: "center", sortable: false, render: (row: User) => <Button
        variant="light-primary"
        size="sm"
        onClick={() => {
          setSelectedData(row)
          setIsUpdateMode(true)
          setShowCreateModal(true)
        }}
        title="financial_feasibility_tab_1"
      >
        <KTIcon iconName="pencil" className="fs-3 pe-0" />
      </Button>
    }
    columns.push(actioncolumns)
  }

  const onCreateUser = () => {
    handleFecthUsers();
  };

  useEffect(() => {
    handleFecthUsers();
    return () => abortRef.current?.abort();
  }, [handleFecthUsers]);

  return (
    <div className="pt-0">
      <div className="gestalt-group border-0 shadow-sm rounded-5 p-6 mb-5">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
          <h1 className="apple-title mb-0">Users</h1>
          <div className="d-flex align-items-center gap-4">
            <CustomFormInput
              name="search"
              value={search || ""}
              disabled={loading}
              onChange={(e) => { setSearch(e.target.value); setPage(1); setPerPage(10); }}
              placeholder="Search here"
              size="sm"
            />
            {auth?.isAdmin == 1 &&
              <Button variant="light-primary flex-shrink-0" size="sm" disabled={loading} onClick={() => setShowCreateModal(true)}>
                <KTIcon iconName='plus-square' className='fs-3 pe-0' /> New User
              </Button>
            }
          </div>
        </div>
      </div>
      <div
        className="bg-white p-6 rounded-1 shadow-sm"
      >
        {loading ? <DataTableSkeleton columns={6} rows={10} />
          : <CustomeDynamicTable columns={columns} data={users} pagination={{
            currentPage: page,
            totalCount: total,
            pageSize: perPage,
            onPageChange: (p) => setPage(p),
            onPageSizeChange: (size) => {
              setPerPage(size);
              setPage(1);
            },
          }}


            sortKey={sortKey}
            sortOrder={sortOrder}
            onSortChange={handleSortChange} />}
      </div>
      <CreateUserModal
        show={showCreateModal}
        onClose={() => {
          setShowCreateModal(false)
          setIsUpdateMode(false)
          setSelectedData(null)
        }}
        onCreateUser={onCreateUser}
        selectedData={selectedData}
        isUpdateMode={isUpdateMode}

      />
    </div>
  );
};

export default UserTable;
